import { Text, Link } from "@chakra-ui/react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { CompanyProfileBanner } from "components/profile/profile-banner";
import useUser from "hooks/use-user";
import type { CompanyTags } from "data/company-tags";
import type { CompanyProfile } from "types/api/company/details/overview";

interface OverviewBannerProps {
  isLoaded: boolean;
  companyProfile: CompanyProfile;
  isInternational: boolean;
  tags: CompanyTags;
  slug: string;
}

const OverviewBanner = ({
  isLoaded,
  companyProfile,
  isInternational,
  tags,
  slug,
}: OverviewBannerProps) => {
  const user = useUser();
  const router = useRouter();
  const isFree = user?.access?.group === "free";
  const isPublic = !user?.isLoggedIn || false;

  const redirectParam = new URLSearchParams({ redirect: router.asPath }).toString();
  const signupRedirect = `/signup?${redirectParam}`;

  const upgradeHref = isPublic ? signupRedirect : isFree ? "/upgrade" : undefined;

  const getUpgradeList = () => {
    const upgradeList = [
      companyProfile?.companyProfileIndex?.indexLatestValuation
        ? "Valuation"
        : null,
      companyProfile?.contacts?.length > 0 ? "Contacts" : null,
    ].filter(Boolean);

    if (!upgradeList.length) {
      return <Text as="b">additional details</Text>;
    }

    const upgradeTextEls = upgradeList.map((item) => (
      <Text key={item} as="b">
        {item}
      </Text>
    ));

    const separatedUpgradeTextEls = upgradeTextEls.reduce(
      (prev, curr, i, arr) => {
        if (i === 0) {
          return [curr];
        }
        let separator = ", ";
        if (i === arr.length - 1) {
          separator = arr.length > 2 ? ", and " : " and ";
        }
        return [prev, separator, curr];
      },
      []
    );

    return separatedUpgradeTextEls;
  };

  const getAccountBannerContent = () => {
    if (!isLoaded) {return null;}

    if (isPublic) {
      return (
        <Text as="span">
          <Link
            as={NextLink}
            href={signupRedirect}
            color="blue.500"
            fontWeight="bold"
            _hover={{ textDecoration: "underline", color: "blue.600" }}
          >
            Sign up now
          </Link>{" "}
          to unlock <Text as="b">additional details</Text> for this profile
        </Text>
      );
    }

    if (isFree) {
      return (
        <Text as="span">
          <Link
            as={NextLink}
            href={upgradeHref}
            color="blue.500"
            fontWeight="bold"
            _hover={{ textDecoration: "underline", color: "blue.600" }}
          >
            Upgrade now
          </Link>{" "}
          to unlock {getUpgradeList()} for this profile
        </Text>
      );
    }

    return null;
  };

  return (
    <CompanyProfileBanner
      isInternational={isInternational}
      tags={tags}
      slug={slug}
    >
      {getAccountBannerContent()}
    </CompanyProfileBanner>
  );
};

export default OverviewBanner;
